import { MAX_DOCUMENTS_PER_PAGE_COUNT } from '@dealstack/shared/lib/dealstackAi/ingestionLimits';
import { chunk } from 'lodash';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
	type CreateIngestionUploadsDto,
	DsaiIngestionProgressStatus,
	type EstimateIngestionDto,
	type IngestionLibraryPageCountsRes,
	type IngestionProgressRes,
	type IngestionUploadListRes,
	type IngestionUploadTargetRes,
	type StartIngestionDto,
} from '@dealstack/shared/lib/generated';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import {
	cancelIngestionBatch,
	completeIngestionUpload,
	countIngestionLibraryPages,
	createIngestionUploads,
	deleteIngestionBatch,
	deleteIngestionFilesNow,
	estimateIngestion,
	getIngestionLibrary,
	getIngestionProgress,
	getIngestionUploads,
	putUploadBody,
	removeIngestionUpload,
	renameIngestionBatch,
	startIngestion,
	warmUpIngestion,
} from '@dealstack/frontend/app/services/dealstackAi/ingestion.service';
import { libraryPageCountsKey } from '@dealstack/frontend/app/services/dealstackAi/ingestion.queryKeys';
import type {
	IngestionBatchReq,
	LibraryQuery,
} from '@dealstack/frontend/app/services/dealstackAi/ingestion.types';
import { createPollingRefetchInterval } from '@dealstack/frontend/shared/dataExchange/reactQuery/pollingRefetchInterval';
import { suppressMutationErrorToast } from '@dealstack/frontend/shared/dataExchange/reactQuery/queryErrorHandler';
import {
	batchListsFilters,
	batchQueriesFilters,
} from '../../AnalysisWorkspace/hooks/useDealstackAiQueries';
import { PROCESSING_POLL_INTERVAL_MS } from '../ingestion.constants';

const PAGE_COUNT_STALE_MS = 5 * 60 * 1000;

/** Processing has ended: complete, cancelled or failed. */
export const isProgressSettled = (progress: IngestionProgressRes | undefined): boolean =>
	progress !== undefined && progress.status !== DsaiIngestionProgressStatus.PROCESSING;

const progressRefetchInterval = createPollingRefetchInterval<IngestionProgressRes>({
	isTerminal: isProgressSettled,
	intervalMs: PROCESSING_POLL_INTERVAL_MS,
});

/** The deal scopes each list; the API layer sends it as the deal-id header, read from the URL. */
const uploadsQueryKey = (dealId: string) => [API_PATHS.DEALSTACK_AI_INGESTION_UPLOADS_GET, dealId];

const libraryQueryKey = (dealId: string, query: LibraryQuery) => [
	API_PATHS.DEALSTACK_AI_INGESTION_LIBRARY_GET,
	dealId,
	query,
];

/** The batch's progress, wherever it is read from. */
export const ingestionProgressFilters = (batchId: string) => ({
	queryKey: [API_PATHS.DEALSTACK_AI_INGESTION_BATCHES_BATCHID_PROGRESS_GET, batchId],
});

export const useIngestionUploadsQuery = (dealId: string, enabled: boolean) =>
	useQuery({
		queryKey: uploadsQueryKey(dealId),
		queryFn: ({ signal }) => getIngestionUploads(signal),
		enabled,
	});

export const useLibraryDocumentsQuery = (dealId: string, query: LibraryQuery, enabled: boolean) =>
	useQuery({
		queryKey: libraryQueryKey(dealId, query),
		queryFn: ({ signal }) => getIngestionLibrary(query, signal),
		enabled,
		placeholderData: keepPreviousData,
	});

export const useLibraryPageCountsQuery = (dealId: string, documentIds: string[]) => {
	const client = useQueryClient();
	const sortedIds = [...new Set(documentIds)].sort();
	return useQuery({
		queryKey: libraryPageCountsKey(dealId, sortedIds),
		queryFn: async ({ signal }) => {
			const chunks = chunk(sortedIds, MAX_DOCUMENTS_PER_PAGE_COUNT);
			const documents: IngestionLibraryPageCountsRes['documents'] = [];
			// Two concurrent requests at most; cached successful chunks survive a failed sibling.
			for (let start = 0; start < chunks.length; start += 2) {
				if (signal.aborted) {
					throw new Error('Page count selection changed');
				}
				const results = await Promise.all(
					chunks.slice(start, start + 2).map((ids) =>
						client.fetchQuery({
							queryKey: [...libraryPageCountsKey(dealId, ids), 'chunk'],
							queryFn: () => countIngestionLibraryPages({ documentIds: ids }),
							staleTime: PAGE_COUNT_STALE_MS,
							retry: false,
						})
					)
				);
				results.forEach((result) => documents.push(...(result?.documents ?? [])));
			}
			return { documents };
		},
		enabled: sortedIds.length > 0,
		retry: false,
	});
};

/** Polled while the batch processes; stops once it has ended, or once polling keeps failing. */
export const useIngestionProgressQuery = (params: IngestionBatchReq, enabled: boolean) =>
	useQuery({
		...ingestionProgressFilters(params.batchId),
		queryFn: ({ signal }) => getIngestionProgress(params.batchId, signal),
		refetchInterval: progressRefetchInterval,
		meta: { suppressErrorToast: true },
		enabled,
	});

export const useCreateUploadsMutation = () =>
	useMutation({
		mutationFn: (body: CreateIngestionUploadsDto) => createIngestionUploads(body),
		onError: suppressMutationErrorToast,
	});

/** Sends a file's bytes to its presigned URL. */
export const usePutUploadMutation = () =>
	useMutation({
		mutationFn: ({ upload, file }: { upload: IngestionUploadTargetRes; file: File }) =>
			putUploadBody(upload, file),
		onError: suppressMutationErrorToast,
	});

/** Has the server count an uploaded file's pages; the counted upload joins the pending list at once. */
export const useCompleteUploadMutation = (dealId: string) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: completeIngestionUpload,
		onError: suppressMutationErrorToast,
		onSuccess: (uploaded) => {
			if (uploaded === undefined) {
				return;
			}
			queryClient.setQueryData<IngestionUploadListRes>(
				uploadsQueryKey(dealId),
				(current) => ({
					files: [
						...(current?.files ?? []).filter((file) => file.fileId !== uploaded.fileId),
						uploaded,
					],
				})
			);
		},
	});
};

export const useRemoveUploadMutation = (dealId: string) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: removeIngestionUpload,
		onError: suppressMutationErrorToast,
		onSettled: () => queryClient.invalidateQueries({ queryKey: uploadsQueryKey(dealId) }),
	});
};

/** Silent: a wake that fails costs only the cold start, not a toast. */
export const useWarmUpIngestionMutation = () =>
	useMutation({
		mutationFn: warmUpIngestion,
		onError: suppressMutationErrorToast,
	});

/** The estimate the confirm shows, read once per "Analyse documents"; its caller shows a failure. */
export const useEstimateIngestionMutation = () =>
	useMutation({
		mutationFn: (body: EstimateIngestionDto) => estimateIngestion(body),
		onError: suppressMutationErrorToast,
	});

/**
 * A started batch takes its uploads off the pending list and joins the deal's batch list. Its caller answers the
 * page-cap refusal with a notice and any other failure with its own toast.
 */
export const useStartIngestionMutation = (dealId: string) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (body: StartIngestionDto) => startIngestion(body),
		onError: suppressMutationErrorToast,
		onSuccess: () =>
			Promise.all([
				queryClient.invalidateQueries({ queryKey: uploadsQueryKey(dealId) }),
				queryClient.invalidateQueries(batchListsFilters()),
			]),
	});
};

/** A write to a started batch; on success it refreshes its progress, the batch and the deal's batch lists. */
const useIngestionBatchMutation = <TVariables, TData>(
	params: IngestionBatchReq,
	write: (variables: TVariables) => Promise<TData>
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: write,
		onError: suppressMutationErrorToast,
		onSuccess: () =>
			Promise.all([
				queryClient.invalidateQueries(ingestionProgressFilters(params.batchId)),
				queryClient.invalidateQueries(batchQueriesFilters(params.batchId)),
				queryClient.invalidateQueries(batchListsFilters()),
			]),
	});
};

export const useCancelIngestionMutation = (params: IngestionBatchReq) =>
	useIngestionBatchMutation(params, () => cancelIngestionBatch(params.batchId));

export const useRenameIngestionBatchMutation = (params: IngestionBatchReq) =>
	useIngestionBatchMutation(params, (title: string) =>
		renameIngestionBatch(params.batchId, { title })
	);

export const useDeleteIngestionFilesNowMutation = (params: IngestionBatchReq) =>
	useIngestionBatchMutation(params, () => deleteIngestionFilesNow(params.batchId));

/** Deletes a failed or empty batch; useDeleteBatch answers it and refreshes the caches. */
export const useDeleteIngestionBatchMutation = (params: IngestionBatchReq) =>
	useMutation({
		mutationFn: () => deleteIngestionBatch(params.batchId),
		onError: suppressMutationErrorToast,
	});
